import { queryOptions } from "@tanstack/react-query";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import {
  fetchTemplateById,
  fetchTemplatePaletteSteps,
  fetchTemplates,
  fetchTemplateSections,
} from "@/domains/templates/api";
import {
  toUiDetail,
  toUiItem,
  toUiPaletteStep,
  toUiSection,
} from "@/domains/templates/mappers";
import type {
  NormalizedTemplateFilterOptions,
  TemplateFilterOptions,
  TemplateStatus,
  UiPaletteStep,
  UiTemplateDetail,
  UiTemplateItem,
  UiTemplateSection,
} from "@/domains/templates/types";

function normalizeTemplateFilterOptions(
  options: TemplateFilterOptions,
): NormalizedTemplateFilterOptions {
  return {
    search: options.search?.trim() ?? "",
    showGlobal: options.showGlobal === true,
  };
}

function normalizeTemplateIds(templateIds: string[]): string[] {
  return [...new Set(templateIds.filter(Boolean))].sort();
}

export const templateBuilderKeys = {
  all: getKeyOnlyQueryConfig(["template-builder"]),
  lists: () => getKeyOnlyQueryConfig(["template-builder", "list"]),
  details: () => getKeyOnlyQueryConfig(["template-builder", "detail"]),
  allSteps: () => getKeyOnlyQueryConfig(["template-builder", "allSteps"]),
};

export const templateBuilderQueries = {
  all: templateBuilderKeys.all,

  lists: templateBuilderKeys.lists,

  list: (options: TemplateFilterOptions, currentTenantId?: string) => {
    const normalizedOptions = normalizeTemplateFilterOptions(options);
    return queryOptions({
      queryKey: [
        ...templateBuilderQueries.lists().queryKey,
        normalizedOptions,
        currentTenantId,
      ],
      queryFn: () =>
        fetchTemplates(normalizedOptions, currentTenantId as string),
      select: (data) => data.map(toUiItem),
      enabled: Boolean(currentTenantId),
    });
  },

  details: templateBuilderKeys.details,

  detail: (id: string) =>
    queryOptions({
      queryKey: [...templateBuilderQueries.details().queryKey, id],
      queryFn: () => fetchTemplateById(id),
      select: (row) => toUiDetail(row),
      enabled: Boolean(id),
    }),

  allSteps: templateBuilderKeys.allSteps,

  steps: (templateId: string) =>
    queryOptions({
      queryKey: [...templateBuilderQueries.allSteps().queryKey, templateId],
      queryFn: () => fetchTemplateSections(templateId),
      select: (rows) => rows.map(toUiSection),
      enabled: Boolean(templateId),
    }),

  paletteSteps: (templateIds: string[]) => {
    const normalizedTemplateIds = normalizeTemplateIds(templateIds);
    return queryOptions({
      queryKey: [
        ...templateBuilderQueries.allSteps().queryKey,
        "palette",
        normalizedTemplateIds,
      ],
      queryFn: () => fetchTemplatePaletteSteps(normalizedTemplateIds),
      select: (rows) => rows.map(toUiPaletteStep),
      enabled: normalizedTemplateIds.length > 0,
    });
  },
};

export type {
  TemplateFilterOptions,
  TemplateFilterOptions as FilterOptions,
  TemplateStatus,
  UiPaletteStep,
  UiTemplateDetail,
  UiTemplateItem,
  UiTemplateSection,
};
